// Shared wording for an onboarding plan. The Add company review screen and the
// Approval page both describe the same plan, so the labels live here once and
// every value is plain text (callers render these strings through dom.ts h()).

import type {
  CompanyBasis,
  CompanyFeaturePack,
  CompanyType,
  OnboardingApprovalKind,
  OnboardingApprovalState,
  OnboardingApprovalTicketStep,
  OnboardingCapabilities,
  OnboardingCapabilityKind,
  OnboardingStepKey,
  OnboardingStepState,
  OnboardingRevealStep,
} from "../types.js";

export function kindLabel(kind: OnboardingCapabilityKind | ""): string {
  if (kind === "property") return "Rental property";
  if (kind === "practice") return "Medical practice";
  if (kind === "other") return "Other business";
  return "not chosen";
}

/** What the plan adds beyond the company itself, in the Add review wording. */
export function capabilitySummary(capabilities: OnboardingCapabilities): string {
  const parts: string[] = [];
  if (capabilities.opsHub) parts.push("the hub");
  if (capabilities.practiceHub) parts.push("the Practice Hub");
  if (capabilities.tenantPortal) parts.push("the tenant portal");
  if (capabilities.moov) parts.push("a wallet");
  if (capabilities.trust) parts.push("a trust");
  if (capabilities.apiKey) parts.push("an API key");
  if (capabilities.statementsDownload) parts.push("statements download on the key");
  return parts.length === 0 ? "no extra services" : parts.join(", ");
}

export function companyTypeLabel(type: CompanyType): string {
  if (type === "gv-internal") return "GV internal";
  if (type === "gvpm-practice") return "GV practice";
  return "external";
}

export function featurePackLabel(pack: CompanyFeaturePack): string {
  return pack === "medical" ? "medical" : "core";
}

export function featurePackSummary(packs: readonly CompanyFeaturePack[]): string {
  return packs.length === 0 ? "none" : packs.map(featurePackLabel).join(", ");
}

export function companyBasisLabel(basis: CompanyBasis): string {
  return basis === "accrual" ? "accrual" : "cash";
}

export function planModeLabel(mode: "new" | "existing"): string {
  return mode === "existing" ? "existing" : "new";
}

export function approvalKindLabel(kind: OnboardingApprovalKind): string {
  return kind === "go_live" ? "Go-live" : "Plan";
}

export function approvalStateLabel(state: OnboardingApprovalState): string {
  if (state === "pending") return "waiting for your approval";
  if (state === "approved") return "approved";
  if (state === "expired") return "expired";
  if (state === "superseded") return "replaced by a newer request";
  return "no longer matches the current plan";
}

export function ticketStepLabel(step: OnboardingApprovalTicketStep): string {
  if (step === "register") return "register the company if it is not registered already";
  if (step === "api_key") return "prepare an API key";
  if (step === "link_invitation") return "prepare a bank-link invitation";
  return "go live";
}

export function revealStepLabel(step: OnboardingRevealStep): string {
  return step === "api_key" ? "prepare an API key" : "prepare a bank-link invitation";
}

export function checklistStateLabel(state: OnboardingStepState): string {
  if (state === "done") return "done";
  if (state === "waiting") return "waiting";
  if (state === "blocked") return "blocked";
  return "failed";
}

/**
 * The business words for a checklist row (rd r13 L3.5).
 *
 * The status contract sends only a key and a title; the Worker's title is
 * engineering language ("Starter chart and company settings merged",
 * "Protection in force", "Publishing access works"). A business owner reads
 * this page, so the portal names each key as the thing that happened. Keys
 * stay unchanged: this is presentation only. A key not listed falls back to
 * the key's own words.
 */
const CHECKLIST_KEY_LABELS: Readonly<Partial<Record<OnboardingStepKey, string>>> = {
  company_registered: "Company registered",
  ledger_created: "Company books set up",
  protection_in_force: "Book protection on",
  ledger_checks_can_run: "Book checks can run",
  starter_merged: "Starting accounts added",
  ledger_checks_green: "Book checks pass",
  publishing_access: "Publishing works",
  // api_key, link_invitation and go_live keep the key's own words: other
  // approved-plan surfaces print them ("Link invitation: blocked"), and they
  // are not the engineering terms this fix names.
  bank_linked: "Bank connected",
  accounts_mapped: "Accounts connected to the books",
  hub_connected: "The hub is connected",
  hub_properties: "Hub properties set",
  hub_accounts: "Hub account list loaded",
  receipt_category_map: "Receipt categories reviewed",
  hub_side_mapping: "Hub connection mapping",
  moov_wallet: "Wallet set up",
  tenant_portal: "Tenant portal set up",
  trust_accounts: "Trust accounts ready",
  statements_option: "Statements download",
  opening_balances: "Opening balances entered",
};

/**
 * Checklist rows in the read response carry only a key and a state (no title),
 * so read the business words above, or humanise the key when it has none
 * rather than inventing a title map.
 */
export function checklistKeyLabel(key: OnboardingStepKey): string {
  const decided = CHECKLIST_KEY_LABELS[key];
  if (decided !== undefined) return decided;
  const words = key.split("_").join(" ");
  return words.length === 0 ? words : `${words[0]?.toUpperCase() ?? ""}${words.slice(1)}`;
}
